import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { deepCopy } from 'deep-copy-ts';
import { finalize, take } from 'rxjs/operators';
import { WindowService } from 'src/app/shared/components/window/window.service';
import { GeospatialService } from '../../shared/geospatial.service';
import { Vertex, VertexClass } from '../../vertexes/state/vertex.model';
import { VertexQuery } from '../../vertexes/state/vertex.query';
import { VertexService } from '../../vertexes/state/vertex.service';
import { createEdge, createUndergroundProperties, Edge, EdgeClass, UndergroundProperties } from '../state/edge.model';
import { EdgeService } from '../state/edge.service';

@Component({
  selector: 'app-edge-form',
  templateUrl: './edge-form.component.html',
  styleUrls: ['./edge-form.component.scss']
})
export class EdgeFormComponent implements OnInit {

  formEdge: FormGroup;

  isLoading: boolean;

  editionMode: boolean;
  edgeClasses: any[];

  vertexA: Vertex;
  vertexB: Vertex;

  removeConfirmated: boolean;

  EdgeClass = EdgeClass;
  MODELS_MAPPER = MODELS_MAPPER;

  properties: object | UndergroundProperties;

  constructor(
    @Inject(MAT_DIALOG_DATA) public edge: Edge,
    private edgeService: EdgeService,
    private formBuilder: FormBuilder,
    private snakBar: MatSnackBar,
    private windowService: WindowService,
    private geospatialService: GeospatialService,
    private vertexQuery: VertexQuery,
    private vertixeService: VertexService
  ) {

    if (this.edge.id) {
      this.editionMode = true;
    }

    this.vertexA = this.vertexQuery.getEntity(this.edge.vertex_a);
    this.vertexB = this.vertexQuery.getEntity(this.edge.vertex_b);

    this.edgeClasses = edgeService.getClasses();

    this.enableClasses();
  }

  get name() {
    return `${this.vertexA.id} - ${this.vertexB.id}`;
  }

  ngOnInit(): void {

    if (this.editionMode) {
      this.isLoading = true;

      this.edgeService.getFull(this.edge.id)
        .pipe(finalize(() => this.isLoading = false))
        .subscribe(v => {
          this.edge = v;
          this.properties = this.edge.properties;
        });
    } else {
      const distance = this.geospatialService.meterDistance(this.edge);
      this.edge.geom_distance = distance;
      this.edge.user_distance = distance;
      this.properties = createUndergroundProperties({});
    }

    this.formEdge = this.formBuilder.group({
      edge_class: [this.edge.edge_class, Validators.required],
      user_distance: [this.edge.user_distance, Validators.required]
    });
  }

  save() {
    if (this.editionMode) {
      this.update();
    } else {
      this.insert();
    }
  }

  remove() {
    if (this.removeConfirmated) {
      this.edgeService.remove(this.edge.id).subscribe(
        v => {
          this.updateVertexes(this.edge);
          this.snakBar.open($localize`Edge "${this.name}" update`, null, { duration: 2000 });
          this.windowService.close(this);
        },
        error => this.snakBar.open($localize`Error on Edge "${this.name}" update`, 'CLOSE'),
      );
    }
    this.removeConfirmated = true;
  }

  updateProperties(properties: object | UndergroundProperties) {
    this.properties = properties;
  }

  private insert() {
    const edge = this.formToData();
    this.edgeService.add(edge).subscribe(
      v => {
        this.updateVertexes(this.edge);
        this.snakBar.open($localize`Edge "${this.name}" created`, null, { duration: 2000 });
        this.windowService.close(this);
      },
      error => this.snakBar.open($localize`Error on Edge "${this.name}" creation`, 'CLOSE'),
    );
  }

  private update() {
    const edge = this.formToData();
    this.edgeService.update(edge.id, edge).subscribe(
      v => {
        this.windowService.close(this);
      },
      error => this.snakBar.open($localize`Error on Edge "${this.name}" update`, 'CLOSE'),
    );
  }

  private formToData() {
    const edgeClass = this.formEdge.get('edge_class').value;
    const userDistance = Number(this.formEdge.get('user_distance').value);
    return createEdge({
      ... this.edge,
      edge_class: edgeClass,
      user_distance: userDistance,
      properties: this.properties
    });
  }

  private enableClasses() {
    if (
      this.isType(this.vertexA, VertexClass.UB) &&
      this.isType(this.vertexB, VertexClass.UB)
    ) {
      this.edgeClasses.find(c => c.id === EdgeClass.U).enabled = true;
    } else if (
      this.isType(this.vertexA, VertexClass.UB) && this.isType(this.vertexB, VertexClass.PL) ||
      this.isType(this.vertexA, VertexClass.PL) && this.isType(this.vertexB, VertexClass.UB)
    ) {
      this.edgeClasses.find(c => c.id === EdgeClass.L).enabled = true;
    } else if (
      this.isType(this.vertexA, VertexClass.UB) && this.isType(this.vertexB, VertexClass.CO) ||
      this.isType(this.vertexA, VertexClass.CO) && this.isType(this.vertexB, VertexClass.UB)
    ) {
      this.edgeClasses.find(c => c.id === EdgeClass.U).enabled = true;
    } else if (
      this.isType(this.vertexA, VertexClass.AC) && this.isType(this.vertexB, VertexClass.PL) ||
      this.isType(this.vertexA, VertexClass.PL) && this.isType(this.vertexB, VertexClass.AC)
    ) {
      this.edgeClasses.find(c => c.id === EdgeClass.A).enabled = true;
    } else if (
      this.isType(this.vertexA, VertexClass.PL) &&
      this.isType(this.vertexB, VertexClass.PL)
    ) {
      this.edgeClasses.find(c => c.id === EdgeClass.A).enabled = true;
    } else if (
      this.isType(this.vertexA, VertexClass.AC) &&
      this.isType(this.vertexB, VertexClass.AC)
    ) {
      this.edgeClasses.find(c => c.id === EdgeClass.A).enabled = true;

    } else {
      this.edgeClasses.forEach(c => c.enabled = true);
    }
  }

  private isType(vertex: Vertex, vertexClass: VertexClass) {
    return vertex.vertex_class === vertexClass;
  }

  private updateVertexes(edge: Edge) {
    this.vertixeService.getFull(edge.vertex_a).pipe(take(1)).subscribe();
    this.vertixeService.getFull(edge.vertex_b).pipe(take(1)).subscribe();
  }
}

const MODELS_MAPPER = {
  Aerial: $localize`Aerial`,
  Underground: $localize`Underground`,
  Lateral: $localize`Lateral`
};

